<template>
    <div class="searchInput" @click="focusInput" @keyup="onKeyUp">
        <input type="text"
               :placeholder="placeholder"
               @input="debounceInput"
               ref="input"
               v-model="searchModel"
               maxlength="200"
               :aria-label="searchAriaLabel" />
        <span v-if="!searchModel.length && !loading">
            <svg width="24" height="24" aria-hidden="true"><use xlink:href="#icon_search"></use></svg>
        </span>
        <button v-if="searchModel.length && !loading"
                type="button"
                class="contacts-block__btn searchInput__clear-btn"
                @click="clearSearchQuery"
                :aria-label="clearSearchAriaLabel">
            <svg width="24" height="24" aria-hidden="true"><use xlink:href="#icon_close"></use></svg>
        </button>
        <span v-if="loading" class="spinner">
            <svg width="24" height="24" aria-hidden="true"><use xlink:href="#icon_loading"></use></svg>
        </span>
        <ul class="searchInput__autocomplete"
            role="menubar"
            v-if="!loading && isAutocomplete && autocompleteVisible">
            <template v-if="groupedAutocomplete.length > 1">
                <li role="none" v-for="(group, n) in groupedAutocomplete" :key="'officeType-'+n">
                    <label :for="'filtered-offices-by-type-' + n">{{ group.name }}</label>
                    <ul role="group" :id="'filtered-offices-by-type-' + n">
                        <li role="none" v-for="(item, i) in group.items" :key="'office-'+n+'-'+i">
                            <button type="button" role="menuitem" @click="autocompleteSelection(item)">
                                {{ formatItem(item) }}
                            </button>
                        </li>
                    </ul>
                </li>
            </template>
            <template v-else>
                <li role="none" v-for="(item, i) in flatAutocomplete" :key="'all-'+i">
                    <button type="button" role="menuitem" @click="autocompleteSelection(item)">
                        {{ formatItem(item) }}
                    </button>
                </li>
            </template>
        </ul>
    </div>
</template>
<script>
    import { debounce as _debounce } from 'lodash';

    export default {
        name: 'SearchInput',
        props: {
            searchQuery: { type: String, default: '' },
            loading: { type: Boolean, default: false },
            autocompleteData: { type: [Array, Object], default: () => [] },
            filteredOfficesByType: { type: Array, default: () => [] },
            autocompleteVisible: { type: Boolean, default: false },
            placeholder: { type: String, default: '' },
            searchAriaLabel: { type: String, default: 'Search' },
            clearSearchAriaLabel: { type: String, default: 'Clear search' },
        },
        data() {
            return {
                searchModel: this.searchQuery || '',
                showMenu: true,
                _bus: null,
                _handlers: {}
            };
        },
        created() {
            // Prefer a shared EventBus on the parent component
            const parent = this.$parent;
            const bus = parent && parent.bus;
            const handlers = this._handlers;
            const self = this;
            const attach = (target) => {
                if (!target || typeof target.$on !== 'function') return;
                handlers.clearAll = () => { self.clearSearchQueryWithoutEmit(); };
                handlers.hideOtherDropdowns = (clickedItem) => {
                    if (clickedItem !== self && self.$el && !self.$el.contains(clickedItem)) {
                        self.showMenu = false;
                    }
                };
                target.$on('clearAll', handlers.clearAll);
                target.$on('hideOtherDropdowns', handlers.hideOtherDropdowns);
                self._bus = target;
            };
            if (bus && typeof bus.$on === 'function') {
                attach(bus);
            } else if (parent && typeof parent.$on === 'function') { // legacy fallback
                attach(parent);
            }
        },
        unmounted() {
            // Cleanup bus listeners if possible
            if (this._bus && typeof this._bus.$off === 'function') {
                const h = this._handlers;
                if (h.clearAll) this._bus.$off('clearAll', h.clearAll);
                if (h.hideOtherDropdowns) this._bus.$off('hideOtherDropdowns', h.hideOtherDropdowns);
            }
            this._handlers = {};
            this._bus = null;
        },
        computed: {
            groupedAutocomplete() {
                return Array.isArray(this.filteredOfficesByType) ? this.filteredOfficesByType : [];
            },
            flatAutocomplete() {
                if (this.groupedAutocomplete.length > 1) return [];
                return Array.isArray(this.autocompleteData) ? this.autocompleteData : [];
            },
            isAutocomplete() {
                const hasGrouped = this.groupedAutocomplete.length > 1;
                const hasFlat = this.flatAutocomplete.length > 0;
                return (hasGrouped || hasFlat) && this.searchModel !== '' && this.showMenu && this.autocompleteVisible;
            }
        },
        watch: {
            searchQuery(newVal) {
                if (newVal !== this.searchModel) {
                    this.searchModel = newVal || '';
                }
            },
            autocompleteVisible(val) {
                if (!val) {
                    this.showMenu = false;
                } else {
                    this.showMenu = true;
                }
            }
        },
        methods: {
            formatItem(item) {
                if (!item) return '';
                const cityPart = item.city ? `${item.city}, ` : '';
                return `${item.name}, ${cityPart}${item.country}`;
            },
            focusInput() {
                if (this.$refs.input) this.$refs.input.focus();
                this.showMenu = true;
            },
            debounceInput: _debounce(function (e) {
                const raw = e.target.value;
                const sanitized = raw.replace(/\0/g, '').trimStart();
                this.searchModel = sanitized;
                this.$emit('search_input', sanitized);
                this.showMenu = true;
            }, 500),
            clearSearchQuery() {
                this.searchModel = '';
                this.$emit('search_input', '');
                this.showMenu = true;
            },
            clearSearchQueryWithoutEmit() {
                this.searchModel = '';
                this.showMenu = true;
            },
            autocompleteSelection(selection) {
                this.$emit('search_autocomplete_selection', selection);
                this.showMenu = true;
            },
            onKeyUp(e) {
                if (e.keyCode === 27 && this.showMenu) { // ESC
                    this.showMenu = false;
                    if (this.$refs.input) this.$refs.input.focus();
                }
            }
        }
    };
</script>